// Public Landing Page (Unauthenticated / Informational & Sign-in)
// Synthwave / Outrun Minimalist Line Aesthetic (70-20-10 Color Architecture)
import { getCurrentProfile, loginWithGoogle } from '../services/authService';
import { navigateTo } from '../router';
import { showToast } from './Toast';
import { getDadDisplayName } from '../types';

export function renderLandingPage(activeMissions: number = 0, connectedDads: number = 0, attendanceRate: number = 100): string {
  const profile = getCurrentProfile();

  return `
    <div class="min-h-screen flex flex-col bg-[#06070a] text-slate-100 selection:bg-cyan-400 selection:text-slate-950 font-sans">
      
      <!-- Public Sticky Header -->
      <header class="bg-[#06070a]/95 border-b border-white/[0.08] sticky top-0 z-40 backdrop-blur-md sticky-header">
        <div class="laser-line-cyan absolute bottom-0 left-0 right-0 opacity-40"></div>
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div class="flex items-center justify-between h-16">
            
            <div class="flex items-center gap-3 cursor-pointer" onclick="window.scrollTo({top: 0, behavior: 'smooth'})">
              <div>
                <div class="flex items-center gap-2">
                  <span class="text-lg font-black tracking-wider uppercase"><span class="text-[#FF2A85]">Pops</span> <span class="text-[#00f0ff]">Culture</span></span>
                </div>
                <p class="text-[11px] text-slate-400 hidden sm:block">Where dad jokes are encouraged</p>
              </div>
            </div>

            <!-- Centered Small Logo: hidden until scrolled past big hero logo -->
            <div id="header-small-logo" class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 pointer-events-none transition-opacity duration-300 ease-in-out cursor-pointer flex items-center justify-center" onclick="window.scrollTo({top: 0, behavior: 'smooth'})" title="Pops Culture">
              <div class="w-12 h-12 rounded-xl flex items-center justify-center p-0.5 hover:shadow-[0_0_18px_rgba(0,240,255,0.5)] transition-all">
                <img src="/logo.png" alt="Pops Culture Logo" class="w-full h-full object-contain">
              </div>
            </div>

            <div class="flex items-center gap-3">
              <a href="#about" class="text-xs font-semibold text-slate-400 hover:text-cyan-400 hidden md:block transition-colors px-3 py-1.5">The Mission</a>
              <a href="#pillars" class="text-xs font-semibold text-slate-400 hover:text-cyan-400 hidden md:block transition-colors px-3 py-1.5">Pillars</a>
              <button id="landing-terms-nav-btn" class="text-xs font-semibold text-slate-400 hover:text-cyan-400 hidden md:block transition-colors px-3 py-1.5 cursor-pointer">Terms & Privacy</button>

              ${profile ? `
                <button id="enter-app-btn" class="btn-primary px-4 py-2 rounded-xl text-xs flex items-center gap-2">
                  <span>Go to the App</span>
                  <i class="fa-solid fa-arrow-right"></i>
                </button>
              ` : `
                <button id="google-login-header-btn" class="bg-transparent hover:bg-white/[0.04] text-slate-100 font-bold px-4 py-2 rounded-xl text-xs flex items-center gap-2 border border-white/[0.15] hover:border-pink-500/60 hover:text-pink-400 hover:shadow-[0_0_14px_rgba(255,42,133,0.3)] transition-all active:scale-95">
                  <i class="fa-brands fa-google text-pink-400"></i>
                  <span>Sign In with Google</span>
                </button>
              `}
            </div>

          </div>
        </div>
      </header>

      <main class="flex-1">
        
        <!-- HERO SECTION -->
        <section class="relative overflow-hidden py-16 sm:py-24 border-b border-white/[0.08] synth-grid-bg">
          <div class="laser-line-pink absolute top-0 left-0 right-0 opacity-40"></div>
          
          <div class="max-w-5xl mx-auto px-4 sm:px-6 text-center relative z-10 space-y-6">
            
            <!-- Large Hero Logo -->
            <div id="hero-big-logo" class="flex justify-center items-center pb-2 animate-fade-in">
              <div class="w-48 h-48 sm:w-60 sm:h-60 md:w-72 md:h-72 flex items-center justify-center relative">
                <div class="absolute inset-0 rounded-full bg-cyan-400/10 blur-3xl pointer-events-none"></div>
                <img src="/logo.png" alt="Pops Culture Logo" class="w-full h-full object-contain relative z-10 drop-shadow-[0_0_28px_rgba(0,240,255,0.35)]">
              </div>
            </div>

            <h1 class="text-4xl sm:text-6xl font-black text-slate-100 tracking-tight leading-tight">
              Dads don't connect over awkward coffee chats.<br>
              <span class="bg-gradient-to-r from-pink-500 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
               We bond side by side on a<br/>shared goal.
              </span>
            </h1>

            <p class="text-base sm:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
              Between family, career, and household upkeep, making time to meet new friends is just hard. <b>Pops Culture</b> eliminates the friction by turning weekend chores, DIY hobbies, and urgent assists into natural bonding catalysts.
            </p>

            <!-- CTA Row -->
            <div class="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
              ${profile ? `
                <button id="hero-enter-btn" class="btn-primary text-sm sm:text-base px-8 py-3.5 rounded-2xl flex items-center gap-3">
                  <span>Welcome Back, ${getDadDisplayName(profile)} — Go to the App</span>
                  <i class="fa-solid fa-arrow-right"></i>
                </button>
              ` : `
                <button id="hero-google-btn" class="bg-[#090c15] text-cyan-300 hover:text-white border border-cyan-400 hover:border-[#38f9d7] font-black text-sm sm:text-base px-8 py-3.5 rounded-2xl flex items-center gap-3 shadow-[0_0_18px_rgba(0,240,255,0.35)] hover:shadow-[0_0_28px_rgba(0,240,255,0.6)] active:scale-95 transition-all">
                  <i class="fa-brands fa-google text-lg text-pink-400"></i>
                  <span>Join Your Local Crew with Google</span>
                </button>
              `}
              
              <a href="#about" class="btn-secondary text-xs sm:text-sm px-6 py-3.5 rounded-2xl flex items-center gap-2">
                <span>Learn How It Works</span>
                <i class="fa-solid fa-chevron-down text-xs"></i>
              </a>
            </div>

            <!-- Live Proof Metrics (Minimalist Laser Line Grid) -->
            <div class="pt-10 grid grid-cols-3 gap-3 sm:gap-6 max-w-xl mx-auto border-t border-white/[0.08] mt-10">
              <div class="p-3 bg-[#0a0c13] rounded-xl border border-white/[0.08] hover:border-cyan-400/40 transition-colors">
                <div class="text-2xl sm:text-3xl font-black text-cyan-400" style="text-shadow: 0 0 10px rgba(0, 240, 255, 0.4);">${activeMissions}</div>
                <div class="text-[11px] text-slate-400 font-semibold uppercase tracking-wider">Active Missions</div>
              </div>
              <div class="p-3 bg-[#0a0c13] rounded-xl border border-white/[0.08] hover:border-cyan-400/40 transition-colors">
                <div class="text-2xl sm:text-3xl font-black text-cyan-400" style="text-shadow: 0 0 10px rgba(0, 240, 255, 0.4);">${connectedDads}</div>
                <div class="text-[11px] text-slate-400 font-semibold uppercase tracking-wider">Connected Dads</div>
              </div>
              <div class="p-3 bg-[#0a0c13] rounded-xl border border-white/[0.08] hover:border-cyan-400/40 transition-colors">
                <div class="text-2xl sm:text-3xl font-black text-cyan-400" style="text-shadow: 0 0 10px rgba(0, 240, 255, 0.4);">${attendanceRate}%</div>
                <div class="text-[11px] text-slate-400 font-semibold uppercase tracking-wider">Attendance Rate</div>
              </div>
            </div>

          </div>
          <div class="laser-line absolute bottom-0 left-0 right-0"></div>
        </section>

        <!-- THE WHY & BEHAVIORAL PSYCHOLOGY -->
        <section id="about" class="py-16 sm:py-20 border-b border-white/[0.08] bg-[#07090f]">
          <div class="max-w-5xl mx-auto px-4 sm:px-6 space-y-10">
            
            <div class="text-center space-y-2">
              <span class="text-xs font-bold text-pink-400 uppercase tracking-widest" style="text-shadow: 0 0 8px rgba(255, 42, 133, 0.4);">Behavioral Psychology</span>
              <h2 class="text-2xl sm:text-3xl font-black text-slate-100">Why Adult Male Socializing Feels Broken</h2>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              <div class="card-workshop p-6 space-y-3 border-white/[0.08] hover:border-pink-500/40">
                <div class="w-10 h-10 rounded-xl bg-transparent border border-pink-500/60 text-pink-400 flex items-center justify-center font-bold shadow-[0_0_10px_rgba(255,42,133,0.25)]">
                  <i class="fa-solid fa-mug-hot"></i>
                </div>
                <h3 class="text-lg font-bold text-slate-100">"Face-to-Face" Social Friction</h3>
                <p class="text-xs sm:text-sm text-slate-300 leading-relaxed">
                  Sitting across a coffee table or bar stool exchanging forced small talk induces unconscious friction. Taking two hours away from family purely for unstructured chit-chat feels hard to justify, leading to chronic cancellations and flaking. Guys just don't make connections this way.
                </p>
              </div>

              <div class="card-workshop p-6 space-y-3 border-cyan-400/40 shadow-[0_0_15px_rgba(0,240,255,0.08)]">
                <div class="w-10 h-10 rounded-xl bg-transparent border border-cyan-400/60 text-cyan-400 flex items-center justify-center font-bold shadow-[0_0_10px_rgba(0,240,255,0.25)]">
                  <i class="fa-solid fa-hammer"></i>
                </div>
                <h3 class="text-lg font-bold text-slate-100">"Shoulder-to-Shoulder" Brotherhood</h3>
                <p class="text-xs sm:text-sm text-slate-300 leading-relaxed">
                  Men develop deep trust naturally when collaborating on a shared objective. When framed around helping a neighbor assemble a shed, haul equipment, volunteer locally. We like to come to the rescue. We also bond most often during a sport or game. It feels more natural to us.
                </p>
              </div>

            </div>

          </div>
        </section>

        <!-- THE 5 PILLARS -->
        <section id="pillars" class="py-16 sm:py-20 border-b border-white/[0.08] relative">
          <div class="laser-line-cyan absolute top-0 left-0 right-0 opacity-30"></div>
          <div class="max-w-6xl mx-auto px-4 sm:px-6 space-y-12">
            
            <div class="text-center space-y-2">
              <span class="text-xs font-bold text-cyan-400 uppercase tracking-widest" style="text-shadow: 0 0 8px rgba(0, 240, 255, 0.4);">Platform Capabilities</span>
              <h2 class="text-2xl sm:text-3xl font-black text-slate-100">Core Pillars Built for Real Fathers</h2>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              <div class="card-workshop p-6 space-y-3 border-white/[0.08] hover:border-amber-400/50">
                <div class="w-10 h-10 rounded-xl bg-transparent border border-amber-400/60 text-amber-400 flex items-center justify-center font-bold shadow-[0_0_10px_rgba(255,183,3,0.25)]">
                  <i class="fa-solid fa-hammer"></i>
                </div>
                <h3 class="text-base font-bold text-slate-100">1. Five-Track Missions</h3>
                <p class="text-xs text-slate-300 leading-relaxed">
                  Hands-on <strong>Projects (🔨)</strong>, get-togethers and <strong>Socials (🍔)</strong>, opportunities for <strong>Volunteering (🤝)</strong>, <strong>Sports (🏀)</strong>, and <strong>Gaming (🎮)</strong> (Dads Only, Kids Welcome, & Family Friendly).
                </p>
              </div>

              <div class="card-workshop p-6 space-y-3 border-white/[0.08] hover:border-pink-500/50">
                <div class="w-10 h-10 rounded-xl bg-transparent border border-pink-500/60 text-pink-400 flex items-center justify-center font-bold shadow-[0_0_10px_rgba(255,42,133,0.25)]">
                  <i class="fa-solid fa-satellite-dish"></i>
                </div>
                <h3 class="text-base font-bold text-slate-100">2. Mayday Beacon Network</h3>
                <p class="text-xs text-slate-300 leading-relaxed">
                  Urgent 2-hour expiring neighborhood pings: <em>"Need 2 guys for 10 minutes to help lift a cast-iron tub onto my truck bed."</em> Instant assists with neighborly gratitude.
                </p>
              </div>

              <div class="card-workshop p-6 space-y-3 border-white/[0.08] hover:border-purple-400/50">
                <div class="w-10 h-10 rounded-xl bg-transparent border border-purple-400/60 text-purple-400 flex items-center justify-center font-bold shadow-[0_0_10px_rgba(168,85,247,0.25)]">
                  <i class="fa-solid fa-id-card"></i>
                </div>
                <h3 class="text-base font-bold text-slate-100">3. The Dad Resume</h3>
                <p class="text-xs text-slate-300 leading-relaxed">
                  Bio, favorite beverage, self-assessed hobby skills, and a privacy-safe <strong>Family Squad</strong> with broad age brackets (e.g., <em>Son (8) - Little League</em>) to coordinate playdates.
                </p>
              </div>

              <div class="card-workshop p-6 space-y-3 border-white/[0.08] hover:border-emerald-400/50">
                <div class="w-10 h-10 rounded-xl bg-transparent border border-emerald-400/60 text-emerald-400 flex items-center justify-center font-bold shadow-[0_0_10px_rgba(0,255,136,0.25)]">
                  <i class="fa-solid fa-medal"></i>
                </div>
                <h3 class="text-base font-bold text-slate-100">4. The Honor Roll</h3>
                <p class="text-xs text-slate-300 leading-relaxed">
                  Camaraderie badges celebrating participation and leadership: <em>First on the Scene</em>, plus milestone badges (1st, 5th, 10th) for attending and hosting across Projects, Socials, Volunteering, Sports, and Gaming.
                </p>
              </div>

            </div>

            <div class="card-workshop p-6 space-y-2 border-cyan-400/30 bg-[#0a0c13] flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div class="w-12 h-12 rounded-xl bg-transparent border border-cyan-400/60 text-cyan-400 flex items-center justify-center font-bold text-xl flex-shrink-0 shadow-[0_0_12px_rgba(0,240,255,0.25)]">
                <i class="fa-solid fa-lock"></i>
              </div>
              <div class="space-y-1">
                <h3 class="text-base font-bold text-slate-100">Privacy & Safety by Design</h3>
                <p class="text-xs text-slate-300 leading-relaxed">
                  Exact home addresses remain hidden and encrypted on public noticeboards, unlocking only when an enlisted dad signs the clickwrap liability waiver and confirms an RSVP.
                </p>
              </div>
            </div>

          </div>
        </section>

        <!-- STRICT FACILITATOR MODEL & LEGAL BOUNDARIES -->
        <section id="legal" class="py-16 sm:py-20 border-b border-white/[0.08] bg-[#07090f]">
          <div class="max-w-4xl mx-auto px-4 sm:px-6 space-y-6">
            
            <div class="p-6 rounded-2xl bg-[#0a0c13] border border-white/[0.08] space-y-4 relative">
              <div class="laser-line absolute top-0 left-0 right-0"></div>
              <div class="flex items-center gap-2 text-cyan-400 font-bold text-sm uppercase tracking-wider" style="text-shadow: 0 0 8px rgba(0, 240, 255, 0.4);">
                <i class="fa-solid fa-scale-balanced text-base"></i>
                <span>Strict Facilitator Legal Operating Model</span>
              </div>
              
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-slate-300 leading-relaxed">
                <div>
                  <strong class="text-slate-100 block mb-1">Strict Communications Tool:</strong>
                  Pops Culture functions exclusively as a volunteer noticeboard. We do not license, vet, or inspect any participant, tool, trade skill, or job site.
                </div>
                <div>
                  <strong class="text-slate-100 block mb-1">Host Sole Authority:</strong>
                  Hosts certify property authority and bear 100% sole responsibility for municipal building permits, HOA covenants, utility markouts (811), and site safety.
                </div>
                <div>
                  <strong class="text-slate-100 block mb-1">Voluntary Assumption of Risk:</strong>
                  Attendees participate as independent voluntary peers who accept personal responsibility for safety and personal protective gear (safety glasses, hearing protection).
                </div>
                <div>
                  <strong class="text-slate-100 block mb-1">Privacy-Safe Geolocation:</strong>
                  Public listings display approximate neighborhoods. Exact street addresses unlock only upon finalized RSVP.
                </div>
                <div class="col-span-full pt-3 border-t border-white/[0.08] flex items-center justify-between flex-wrap gap-2 text-xs">
                  <span class="text-slate-400">Review our comprehensive policies and community protections:</span>
                  <a href="/terms" class="text-cyan-400 hover:text-cyan-300 font-bold hover:underline inline-flex items-center gap-1.5 cursor-pointer">
                    <i class="fa-solid fa-file-contract"></i> View Full Terms & Privacy Policy &rarr;
                  </a>
                </div>
              </div>
            </div>

          </div>
        </section>

        <!-- CONTACT & ORGANIZERS -->
        <section class="py-16 border-b border-white/[0.08]">
          <div class="max-w-3xl mx-auto px-4 sm:px-6 text-center space-y-4">
            <h3 class="text-xl font-black text-slate-100">Local Crew & Community Contact</h3>
            <p class="text-xs text-slate-400 max-w-lg mx-auto">
              Have questions about organizing a project in your neighborhood, hands-on tasks, or connecting with local organizers?
            </p>
            <a href="mailto:cory@popsculture.dad" class="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-transparent border border-white/[0.12] text-xs text-slate-300 hover:border-cyan-400/40 hover:text-cyan-300 transition-colors">
              <i class="fa-regular fa-envelope text-cyan-400"></i>
              <span>cory@popsculture.dad</span>
            </a>
          </div>
        </section>

      </main>

      <!-- Footer with Facilitator Operating Model & Terms & Privacy Policy Link -->
      <footer class="bg-[#06070a] border-t border-white/[0.08] text-slate-400 text-xs py-10 px-4 sm:px-6 lg:px-8 relative" id="legal">
        <div class="laser-line-cyan absolute top-0 left-0 right-0"></div>
        <div class="max-w-7xl mx-auto space-y-6">
          <div class="p-4 rounded-xl bg-[#0a0c13] border border-white/[0.08] space-y-2">
            <div class="flex items-center gap-2 text-cyan-400 font-bold text-xs uppercase tracking-wider">
              <i class="fa-solid fa-scale-balanced"></i> Platform Scope & Legal Operating Model
            </div>
            <p class="text-[11px] leading-relaxed text-slate-400">
              <strong class="text-slate-200">Pops Culture is strictly an open communication and social introduction platform.</strong> 
              We provide an online noticeboard facilitating voluntary gatherings and mutual discovery among local fathers. Pops Culture does not inspect, vet, verify, or background-check any user, their trade skills, or tools. Furthermore, Pops Culture does not inspect, engineer, oversee, or approve any project, structure, work site, or physical activity.
            </p>
          </div>
          <div class="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2 border-t border-white/[0.06] text-[11px] text-slate-500">
            <div class="flex items-center gap-2">
              <span class="font-bold text-slate-300">Pops Culture</span> &copy; 2026. Shoulder-to-Shoulder Community.
            </div>
            <div class="flex items-center gap-4">
              <button id="landing-footer-terms-btn" class="hover:text-cyan-400 transition-colors cursor-pointer" title="View full list of Terms and Privacy">Terms & Privacy Policy</button>
            </div>
          </div>
        </div>
      </footer>

    </div>
  `;
}

export function bindLandingPageEvents(onOpenTerms?: () => void): void {
  // Terms of Service buttons (header and footer)
  const openTerms = () => {
    if (onOpenTerms) {
      onOpenTerms();
    } else {
      navigateTo('/terms');
    }
  };

  document.getElementById('landing-terms-nav-btn')?.addEventListener('click', openTerms);
  document.getElementById('landing-footer-terms-btn')?.addEventListener('click', openTerms);

  const loginBtns = [
    document.getElementById('google-login-header-btn'),
    document.getElementById('hero-google-btn')
  ];

  loginBtns.forEach(btn => {
    btn?.addEventListener('click', async () => {
      try {
        const profile = await loginWithGoogle();
        showToast(`Welcome to Pops Culture, ${getDadDisplayName(profile)}!`);
        navigateTo('/app');
      } catch (err) {
        showToast('Google Sign-In was cancelled or failed.', 'warning');
      }
    });
  });

  const enterBtns = [
    document.getElementById('enter-app-btn'),
    document.getElementById('hero-enter-btn')
  ];

  enterBtns.forEach(btn => {
    btn?.addEventListener('click', () => {
      navigateTo('/app');
    });
  });

  // Scroll listener: fade in small header logo only when scrolled past big hero logo
  const bigLogo = document.getElementById('hero-big-logo');
  const smallLogo = document.getElementById('header-small-logo');

  if (bigLogo && smallLogo) {
    const handleScroll = () => {
      const rect = bigLogo.getBoundingClientRect();
      // When the bottom of the big logo has scrolled above the sticky header (64px)
      if (rect.bottom <= 64) {
        smallLogo.classList.remove('opacity-0', 'pointer-events-none');
        smallLogo.classList.add('opacity-100', 'pointer-events-auto');
      } else {
        smallLogo.classList.remove('opacity-100', 'pointer-events-auto');
        smallLogo.classList.add('opacity-0', 'pointer-events-none');
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
  }
}
